/* eslint-disable lingui/no-unlocalized-strings */
import { CheckCircle, CircleNotch, Target } from "@phosphor-icons/react";
import { Badge, Button, Card } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";

import { useRtsScore } from "@/client/services/resume/rts-score";
import { useResumeStore } from "@/client/stores/resume";
import { toast } from "@/client/hooks/use-toast";

import { getSectionIcon } from "../shared/section-icon";

const getScoreColor = (score: number) => {
  if (score <= 40) return "text-error";
  if (score <= 70) return "text-warning";
  return "text-success";
};

const getScoreGradient = (score: number) => {
  if (score <= 40) return "from-error to-error/60";
  if (score <= 70) return "from-warning to-warning/60";
  return "from-success to-success/60";
};

const getVerdictLabel = (verdict: string) => {
  switch (verdict) {
    case "needs_improvement":
      return "Needs Improvement";
    case "decent_match":
      return "Decent Match";
    case "strong_match":
      return "Strong Match";
    default:
      return "Unknown";
  }
};

export const RtsScoreSection = () => {
  const id = useResumeStore((state) => state.resume.id);
  const [jobDescription, setJobDescription] = useState("");
  const [showResults, setShowResults] = useState(false);

  const { mutate: calculateScore, data: rtsData, isPending: loading, error } = useRtsScore();

  const handleAnalyze = () => {
    if (!id) {
      toast({
        variant: "error",
        title: "Resume Not Loaded",
        description: "Please wait for the resume to load before analyzing.",
      });
      return;
    }

    if (!jobDescription.trim() || jobDescription.trim().length < 10) {
      toast({
        variant: "error",
        title: "Invalid Job Description",
        description: "Please enter a job description with at least 10 characters.",
      });
      return;
    }

    calculateScore(
      { id, jobDescription: jobDescription.trim() },
      {
        onSuccess: () => {
          setShowResults(true);
        },
        onError: (error: any) => {
          toast({
            variant: "error",
            title: "Failed to Calculate RTS Score",
            description: error?.response?.data?.message || error?.message || "An error occurred. Please try again.",
          });
        },
      },
    );
  };

  const score = rtsData?.score ?? 0;
  const verdict = rtsData?.verdict ?? "needs_improvement";
  const matchedKeywords = rtsData?.matchedKeywords ?? [];
  const missingKeywords = rtsData?.missingKeywords ?? [];
  const explanation = rtsData?.explanation ?? "";

  // Calculate progress percentage for circular indicator
  const progressPercentage = score;
  const circumference = 2 * Math.PI * 36; // radius = 36
  const offset = circumference - (progressPercentage / 100) * circumference;

  return (
    <section id="rts-score" className="grid gap-y-6">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-x-4">
          {getSectionIcon("statistics")}
          <h2 className="line-clamp-1 text-3xl font-bold">RTS Score</h2>
        </div>
      </header>

      <main className="grid gap-y-6">
        {/* Job Description Input */}
        <div className="space-y-3">
          <label className="text-sm font-medium opacity-75">Target Job Description</label>
          <textarea
            value={jobDescription}
            onChange={(e) => setJobDescription(e.target.value)}
            placeholder="Paste the job description here to analyze how well your resume matches..."
            className="min-h-[120px] w-full rounded-sm border border-border bg-background px-3 py-2 text-sm placeholder:opacity-80 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50"
            disabled={loading}
          />
          <Button
            onClick={handleAnalyze}
            disabled={loading || !id || !jobDescription.trim() || jobDescription.trim().length < 10}
            className="w-full"
            variant="primary"
          >
            {loading ? (
              <>
                <CircleNotch className="mr-2 animate-spin" size={16} />
                Analyzing...
              </>
            ) : (
              <>
                <Target className="mr-2" size={16} />
                Analyze ATS Score
              </>
            )}
          </Button>
        </div>

        {/* Results */}
        <AnimatePresence>
          {showResults && rtsData && (
            <motion.div
              initial={{ opacity: 0, y: 20, filter: "blur(10px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              exit={{ opacity: 0, y: -20, filter: "blur(10px)" }}
              transition={{ duration: 0.3 }}
            >
              <Card className="bg-secondary/50 p-6">
                {/* Score Display */}
                <div className="mb-6 flex items-center justify-between">
                  <div>
                    <p className="text-sm opacity-75">Resume-Target Suitability</p>
                    <p className="text-xs opacity-60">Fit with this role</p>
                  </div>
                  <div className="relative h-20 w-20">
                    <svg className="h-20 w-20 -rotate-90 transform">
                      <circle
                        cx="40"
                        cy="40"
                        r="36"
                        stroke="currentColor"
                        strokeWidth="6"
                        fill="none"
                        className="opacity-20"
                      />
                      <circle
                        cx="40"
                        cy="40"
                        r="36"
                        stroke="currentColor"
                        strokeWidth="6"
                        fill="none"
                        strokeDasharray={circumference}
                        strokeDashoffset={offset}
                        strokeLinecap="round"
                        className={cn("transition-all duration-1000", getScoreColor(score))}
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className={cn("text-2xl font-bold", getScoreColor(score))}>{score}</span>
                    </div>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mb-4 flex items-center gap-2">
                  <div className="h-2 flex-1 overflow-hidden rounded-full bg-secondary">
                    <div
                      className={cn(
                        "h-full rounded-full bg-gradient-to-r transition-all duration-1000",
                        getScoreGradient(score),
                      )}
                      style={{ width: `${score}%` }}
                    />
                  </div>
                  <span className={cn("text-sm font-medium", getScoreColor(score))}>
                    {getVerdictLabel(verdict)}
                  </span>
                </div>

                {/* Explanation - Dynamic content from API */}
                {explanation && (
                  <p className="mb-4 text-xs leading-relaxed opacity-75">
                    {explanation}
                  </p>
                )}

                {/* Matched Keywords */}
                {matchedKeywords.length > 0 && (
                  <div className="mb-4">
                    <div className="mb-2 flex items-center justify-between">
                      <h3 className="text-sm font-semibold">Matched Keywords</h3>
                      <span className={cn("text-xs font-medium", getScoreColor(score))}>
                        {matchedKeywords.length} found
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {matchedKeywords.map((keyword, index) => (
                        <Badge key={index} variant="success" outline>
                          <CheckCircle size={12} className="mr-1" />
                          {keyword}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Missing Keywords */}
                {missingKeywords.length > 0 && (
                  <div>
                    <div className="mb-2 flex items-center justify-between">
                      <h3 className="text-sm font-semibold">Missing Keywords</h3>
                      <span className="text-xs font-medium text-error">
                        {missingKeywords.length} missing
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {missingKeywords.map((keyword, index) => (
                        <Badge key={index} variant="error" outline>
                          {keyword}
                        </Badge>
                      ))}
                    </div>
                    <p className="mt-2 text-xs opacity-60">
                      Consider adding these relevant keywords to improve your score
                    </p>
                  </div>
                )}
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Error State */}
        {error && !loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="rounded-sm border border-error/50 bg-error/10 p-3 text-sm text-error"
          >
            Failed to calculate RTS score. Please try again.
          </motion.div>
        )}
      </main>
    </section>
  );
};
